import React from 'react';
import Icon from '../../../components/AppIcon';

const ResourceConflicts = ({ events }) => {
  // Mock conflict detection logic
  const detectConflicts = () => {
    const conflicts = [];
    
    // Example conflicts based on availability events
    const lowAvailability = events?.filter(e => e?.type === 'availability' && e?.availabilityLevel === 'low');
    
    lowAvailability?.forEach(event => {
      conflicts?.push({
        id: `conflict-${event?.id}`,
        type: 'low_availability',
        severity: 'high',
        department: event?.department,
        description: `${event?.department?.toUpperCase()} department has only ${event?.availableStaff}/${event?.totalStaff} staff available`,
        date: event?.date,
        recommendation: 'Consider rescheduling non-critical tasks or requesting support from other departments'
      });
    });

    // Add mock conflicts for demonstration
    if (conflicts?.length === 0) {
      conflicts?.push(
        {
          id: 'conflict-1',
          type: 'overlapping_leave',
          severity: 'medium',
          department: 'finance',
          description: '3 team members on leave during month-end closing',
          date: new Date(2026, 0, 30),
          recommendation: 'Ensure critical tasks are delegated before leave period'
        },
        {
          id: 'conflict-2',
          type: 'deadline_clash',
          severity: 'low',
          department: 'hr',
          description: 'Training event scheduled on same day as NASFUND deadline',
          date: new Date(2026, 0, 15),
          recommendation: 'Reschedule training to avoid deadline conflicts'
        }
      );
    }

    return conflicts;
  };

  const conflicts = detectConflicts();

  const getSeverityColor = (severity) => {
    const colors = {
      high: 'text-error',
      medium: 'text-warning',
      low: 'text-primary'
    };
    return colors?.[severity] || 'text-muted-foreground';
  };

  const getSeverityBg = (severity) => {
    const colors = {
      high: 'bg-error/10 border-error',
      medium: 'bg-warning/10 border-warning',
      low: 'bg-primary/10 border-primary'
    };
    return colors?.[severity] || 'bg-muted border-border';
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      high: 'AlertTriangle',
      medium: 'AlertCircle',
      low: 'Info'
    };
    return icons?.[severity] || 'Circle';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
        <h3 className="font-heading font-semibold text-foreground">Resource Conflicts</h3>
      </div>
      <div className="space-y-3">
        {conflicts?.length === 0 ? (
          <div className="text-center py-6">
            <Icon name="CheckCircle" size={32} color="var(--color-success)" className="mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No resource conflicts detected</p>
          </div>
        ) : (
          conflicts?.map((conflict) => (
            <div
              key={conflict?.id}
              className={`p-3 rounded-lg border ${getSeverityBg(conflict?.severity)}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <Icon 
                  name={getSeverityIcon(conflict?.severity)} 
                  size={16} 
                  className={getSeverityColor(conflict?.severity)} 
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-medium uppercase ${getSeverityColor(conflict?.severity)}`}>
                      {conflict?.severity}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {conflict?.department?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    {conflict?.description}
                  </p>
                </div>
              </div>

              {conflict?.recommendation && (
                <div className="mt-2 p-2 bg-muted/50 rounded text-xs text-muted-foreground">
                  <Icon name="Lightbulb" size={12} className="inline mr-1" />
                  {conflict?.recommendation}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ResourceConflicts;